/**
 * Maps reference-data `resume_upload_process_labels` [{ id, name }, ...]
 * into UI meta for the resume upload / processing modal.
 * The last sorted entry is treated as the completion milestone; all prior entries are checklist rows.
 */
export function buildResumeUploadProgressMeta(rows) {
  const defaultMeta = {
    uiStepCount: 4,
    maxListStepIndex: 3,
    listLabels: [],
    labelForStepId: () => '',
    fallbackChainLabel: 'Processing…',
  };

  if (!Array.isArray(rows) || rows.length === 0) {
    return defaultMeta;
  }

  const sorted = [...rows]
    .map((r) => ({
      id: typeof r?.id === 'number' ? r.id : Number.parseInt(String(r?.id), 10),
      name: typeof r?.name === 'string' ? r.name : '',
    }))
    .filter((r) => Number.isFinite(r.id) && !Number.isNaN(r.id))
    .sort((a, b) => a.id - b.id);

  if (sorted.length === 0) {
    return defaultMeta;
  }

  const idToName = (id) => sorted.find((x) => x.id === id)?.name ?? '';

  const listEntries = sorted.length > 1 ? sorted.slice(0, -1) : sorted;
  const uiStepCount = Math.max(1, listEntries.length);
  const listLabels = listEntries.map((e) => e.name);

  return {
    uiStepCount,
    maxListStepIndex: uiStepCount - 1,
    listLabels,
    labelForStepId: idToName,
    fallbackChainLabel:
      idToName(sorted[0].id) || defaultMeta.fallbackChainLabel,
  };
}

/** Step ids below this use no per-step reference label in the subtitle (API `label` still wins). */
export const RESUME_PROGRESS_SUBTITLE_REFERENCE_STEP_MIN = 2;

/**
 * Fallback chain: upload-processing `label` wins, then reference label for `step` (if step is high enough), then fallback.
 */
export function getResumeProgressDisplayLabel({
  status,
  step,
  label,
  labelForStepId,
  fallbackLabel,
  /** Omit per-step reference text when `step` is below this (default: first two steps 0 and 1). */
  minStepForReferenceSubtitle = RESUME_PROGRESS_SUBTITLE_REFERENCE_STEP_MIN,
}) {
  if (status === 'failed') {
    return "We couldn't process your resume";
  }
  const s = typeof step === 'number' && !Number.isNaN(step) ? step : 0;
  const useRefForStep =
    s >= minStepForReferenceSubtitle && typeof labelForStepId === 'function';
  const fromRef = useRefForStep ? labelForStepId(s) : '';
  return label || fromRef || fallbackLabel || 'Processing…';
}
